import { FC, useMemo } from 'react';

import { AdminItem, resolveImageSrc } from '../../../api/client';
import { formatMoney } from '../../../shared/functions/formatter';
import { Button, Dialog, Input, Meta } from '../../../ui';
import { plural } from './types';

type BundleEligibleItemPickerProps = {
  open: boolean;
  items: AdminItem[];
  selectedIds: number[];
  search: string;
  onSearchChange: (value: string) => void;
  onToggle: (itemId: number) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * The selected-items picker: search the catalogue and check the members of one
 * group. It is a domain component rather than a generic dialog body because it
 * knows how a bundle names and prices an eligible item.
 */
const BundleEligibleItemPicker: FC<BundleEligibleItemPickerProps> = ({
  open,
  items,
  selectedIds,
  search,
  onSearchChange,
  onToggle,
  onCancel,
  onConfirm,
}) => {
  const filtered = useMemo(() => items.filter((item) => item.title.toLowerCase().includes(search.trim().toLowerCase())), [items, search]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title="Select items"
      description="Choose the items this group can draw from."
      size="lg"
      className="discountPicker"
      actions={
        <>
          <span className="discountPickerCount">{plural(selectedIds.length, 'item')} selected</span>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Done
          </Button>
        </>
      }
    >
      <Input
        className="discountPickerSearch"
        type="search"
        value={search}
        placeholder="Search items"
        aria-label="Search items"
        onChange={(event) => onSearchChange(event.target.value)}
      />
      <div className="discountPickerList">
        {filtered.map((item) => (
          <label className="discountPickerItem" key={item.id}>
            <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => onToggle(item.id)} />
            {item.image_url || item.img ? (
              <img className="discountPickerThumb" src={resolveImageSrc(item.image_url || item.img)} alt="" />
            ) : (
              <span className="discountPickerThumb discountGroupThumbEmpty" />
            )}
            <span className="discountPickerName">{item.title}</span>
            <span className="discountPickerPrice money">{formatMoney(item.price_cents)}</span>
          </label>
        ))}
        {filtered.length === 0 && <Meta>No items match.</Meta>}
      </div>
    </Dialog>
  );
};

export default BundleEligibleItemPicker;
